/**
 * Helper para gestión de caché en localStorage
 * Reutilizable para todos los catálogos
 */

const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 horas

export interface CacheItem<T> {
  data: T[];
  timestamp: number;
}

/**
 * Obtiene datos del localStorage con validación de expiración
 */
export function getFromCache<T>(key: string): T[] | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const cached: CacheItem<T> = JSON.parse(item);
    const now = Date.now();

    if (now - cached.timestamp > CACHE_EXPIRATION) {
      localStorage.removeItem(key);
      return null;
    }

    return cached.data;
  } catch {
    return null;
  }
}

/**
 * Guarda datos en localStorage con timestamp
 */
export function saveToCache<T>(key: string, data: T[]): void {
  try {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cacheItem));
  } catch (error) {
    console.error(`Error al guardar ${key} en caché:`, error);
  }
}

/**
 * Detecta si un objeto es un valrep (objeto plano con claves cortas)
 * Ejemplo: { b: "Bonos", d: "Domiciliaria", m: "Membresia" }
 */
function isValrepObject(obj: any): boolean {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return false;
  }

  const keys = Object.keys(obj);
  // Un valrep tiene múltiples claves cortas (1-2 caracteres) con valores string
  return keys.length > 0 && keys.every(key =>
    key.length <= 2 && typeof obj[key] === 'string'
  );
}

/**
 * Transforma un objeto valrep en array normalizado
 * Entrada: { b: "Bonos", d: "Domiciliaria" }
 * Salida: [{ id: "b", value: "Bonos" }, { id: "d", value: "Domiciliaria" }]
 */
function transformValrepToArray(valrepObj: any): any[] {
  if (!valrepObj || typeof valrepObj !== 'object') {
    return [];
  }

  return Object.keys(valrepObj)
    .filter(key => key) // Filtrar keys vacías o undefined
    .map(key => ({
      id: key,
      value: valrepObj[key] || ''
    }));
}

/**
 * Normaliza las keys a minúsculas y transforma valreps
 */
export function normalizeKeys<T>(items: any): T[] {
  // Si es un solo objeto valrep, transformarlo
  if (isValrepObject(items)) {
    return transformValrepToArray(items) as T[];
  }

  // Si es un array con un solo elemento que es valrep
  if (Array.isArray(items) && items.length === 1 && isValrepObject(items[0])) {
    return transformValrepToArray(items[0]) as T[];
  }

  // Si no es array, convertir a array
  const itemsArray = Array.isArray(items) ? items : [items];

  // Normalizar keys a minúsculas
  return itemsArray.map(item => {
    if (!item || typeof item !== 'object') {
      return item as T;
    }

    const obj: any = {};
    Object.keys(item).forEach(key => {
      if (key) {
        obj[key.toLowerCase()] = item[key];
      }
    });
    return obj as T;
  });
}

/**
 * Filtra datos en memoria según parámetros
 * Soporta filtrado de valreps normalizados {id, value}
 */
export function filterInMemory<T>(data: T[], params?: Record<string, any>): T[] {
  if (!params || Object.keys(params).length === 0) {
    return data;
  }

  return data.filter(item => {
    return Object.keys(params).every(key => {
      const itemKey = key.toLowerCase();
      const paramValue = params[key];
      const itemValue = (item as any)[itemKey];

      // Si el item tiene estructura valrep {id, value}, intentar matchear por id
      if ((item as any).id !== undefined && (item as any).value !== undefined) {
        // Si estamos buscando por ctipo/codigo, comparar con id
        if (itemKey === 'ctipo' || itemKey === 'codigo') {
          return (item as any).id === paramValue;
        }
      }

      // Comparación normal
      return itemValue === paramValue;
    });
  });
}

/**
 * Limpia el caché de un catálogo específico
 */
export function clearCache(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error al limpiar caché ${key}:`, error);
  }
}

/**
 * Limpia todo el caché de catálogos
 */
export function clearAllCatalogos(): void {
  const catalogKeys = [
    'catalogo_paises',
    'catalogo_estados',
    'catalogo_ciudades',
    'catalogo_sectores',
    'catalogo_etiquetas',
  ];

  catalogKeys.forEach(key => clearCache(key));
}
